
import React, { useContext, useState, useEffect, useCallback, useReducer, useMemo } from 'react'

import { createPortal } from 'react-dom';

import { Dialog, DialogTitle, ListItem, Button, DialogActions, Divider, DialogContent, Box, Tooltip, TextField, IconButton, FormControl, InputLabel, Select, MenuItem } from '@mui/material'

import ResponsiveImportButton from '../../../Clients/ResponsiveImportButton'

import useNotification from '../../../Notification'
import api from '../../../../api'
import { getSession } from "../../../../auth.js";
import { ScreenContext } from "../.."
import { QueueSocket } from '../../../../routes'
import CloseIcon from '@mui/icons-material/Close';
import LoadingButton from '@mui/lab/LoadingButton';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import Loading from '../../../Loading'
import HelpIcon from '@mui/icons-material/Help';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import { FixedSizeList } from 'react-window';
import AutoSizer from "react-virtualized-auto-sizer"
import { clearImportedText, normalizeImportHeader, readImportedFileAsText, replicateFirstRowSegments } from '../import-utils'
import { isValidCnpj, normalizeCnpj } from '../../../../utils/masks'

import './styles.css'

const numberRexed = /^[^0-9+]|(?<=.)[^0-9]/g
const onlyDigits = value => (value || '').replace(/[^0-9]+/g, '')
const formatCnpj = (value) => {
	const cleaned = normalizeCnpj(value).slice(0, 14)
	return cleaned
}

const formatBirthday = (value) => {
	const str = (value || '').trim()
	if (!str) return ''

	if (/^\d{2}\/\d{2}\/\d{4}$/.test(str)) {
		return str
	}

	if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
		const [year, month, day] = str.split('-')
		return `${day}/${month}/${year}`
	}

	if (/^\d{4}\/\d{2}\/\d{2}$/.test(str)) {
		const [year, month, day] = str.split('/')
		return `${day}/${month}/${year}`
	}

	return str
}

const enterprise = getSession()?.profile.enterprise
const importLimit = enterprise?.settings?.crm_upload_limit || 5000

const validate = (item, pos) => {
	const errors = []
	switch (pos) {
		case 'nome':
			if (item.nome.length > 100) {
				errors.push('Cada nome só pode ter no máximo 100 caracteres.')
			}
			break
		case 'email':
			if (item.email.length > 100) {
				errors.push('Cada email só pode ter no máximo 100 caracteres.')
			}
			break

		case 'segmentos':
			item.segmentos.split(',').some(each => {
				if (each.length > 50)
					errors.push('Cada segmento só pode ter no máximo 50 caracteres.')
			})
			break
		case 'customer_code':
			if (item.customer_code?.length > 100) {
				errors.push('Cada código só pode ter no máximo 100 caracteres.')
			}
			break
		case 'telefone':
				const telefone = item.telefone
				const temMais = telefone.includes('+');
				const comecaComMais = telefone.startsWith('+');

				// Regra 1: tamanho máximo absoluto
				if (telefone.length > 15) {
					errors.push('Telefone não pode ter mais de 15 caracteres.');
				}

				// Regra 2: se tiver +, deve ser o primeiro caractere
				if (temMais && !comecaComMais) {
					errors.push('O + só pode ser o primeiro caractere do telefone.');
				}

				// Regra 3: se tiver +, deve começar com +55
				if (comecaComMais && !telefone.startsWith('+55')) {
					errors.push('Telefones com + devem começar com +55.');
				}

				// Regra 4: se não tiver +, só pode ter até 14 caracteres
				if (!temMais && telefone.length > 14) {
					errors.push('Telefone sem + só pode ter até 14 caracteres.');
				}

				// Regra 5: se tiver + mas não tiver 55 depois
				if (comecaComMais && !telefone.startsWith('+55')) {
					errors.push('Telefone com + deve conter o código 55 logo após.');
				}

				// Regra 6: se não tiver o +55, limite 12 caracteres
				if (!telefone.startsWith('55') && telefone.length > 12 && !temMais) {
					errors.push('Telefone sem 55 deve conter no máximo 12 caracteres.');
				}

			const chars = item.telefone.match(numberRexed)
			if (chars)
				errors.push(`Existem caracteres inválidos: ${chars.join(', ')}`)
			break
		case 'cpf':
			if (item.cpf && item.cpf.match(/[^0-9]/)) {
				errors.push('CPF deve conter apenas números.')
			}
			break
		case 'cnpj':
			if (item.cnpj && !isValidCnpj(item.cnpj)) {
				errors.push('CNPJ inválido.')
			}
			break
		case 'birthday':
			if (item.birthday && item.birthday.length > 50) {
				errors.push('Formato de aniversário inválido.')
			}
			break
		default:
			break
	}
	if (errors.length === 0) {
		return ''
	}

	return errors.map(erro => (<Box>{erro}</Box>))
}

const revalidate = (data) => {

	if (data.length > importLimit) {
		useNotification(
			'Ops!',
			`Limite de ${importLimit} contatos excedido.`,
			'danger'
		);
		data = data.slice(0, importLimit)
	}
	data.map(row => {
		const erros = ['nome', 'email', 'telefone', 'codigo', 'segmentos', 'cpf', 'cnpj', 'birthday'].some(pos => validate(row, pos))
		row.error = erros
	})

	const newdata = data.sort((x, y) => Boolean(y.error) - Boolean(x.error))
	return newdata
}

const reposition = (data) => {
	data.some((each, index) => { each.position = index })
	return data
}

const maxLengthPos = {
	'segmentos': 99999,
	'telefone': 15,
	'email': 100,
	'customer_code': 100,
	'nome': 100,
	'enterprise_name': 200,
	'cpf': 14,
	'cnpj': 14,
	'birthday': 50
}

const RowEdit = ({ item, pos, label, revalidate }) => {
	const [editing, setEditing] = useState(false)
	const [loadeditem, setLoadedItem] = useState(item[pos])
	const [error, setError] = useState([])

	const submit = () => {
		const normalized = pos === 'cnpj' ? normalizeCnpj(loadeditem).slice(0, 14) : loadeditem
		setEditing(false)
		item[pos] = normalized
		setLoadedItem(normalized)
		revalidate()
	}

	useEffect(() => {
		setError(validate(item, pos))
	}, [editing])

	return (
		<Tooltip title={(error)} describeChild={true}>
			<span className={error ? 'error' : ''}>
				{!editing ?
					<>
						<Box>{pos === 'cnpj' ? formatCnpj(loadeditem) : (pos === 'birthday' ? formatBirthday(loadeditem) : loadeditem)}</Box>
						<Tooltip title={label}><Button onClick={() => setEditing(true)} className='action-button'>
							<EditIcon />
						</Button>
						</Tooltip>
					</>

					:
					<>
						<TextField
							className='edit-import-input'
							value={loadeditem}
							variant='standard'
							onChange={(e) => {
								if (pos === 'cnpj') {
									setLoadedItem(normalizeCnpj(e.target.value).slice(0, 14))
									return
								}
								setLoadedItem(e.target.value)
							}}
							inputProps={{
								maxLength: maxLengthPos[pos]
							}}
						/>
						<Tooltip title={'Salvar'}><Button onClick={submit} className='action-button'><SaveIcon /></Button></Tooltip>
					</>
				}
			</span>
		</Tooltip>

	)
}


const ImportClients = ({ usersEmails }) => {
	const { getGroups, getClients, enterpriseId, updateReady, permissions, settings, customFields } = useContext(ScreenContext)
	const { cache, setCache, failedContacts, setFailedContacts } = useContext(QueueSocket)
	const [selectedClients, updateSelectedClients] = useState([])
	const [separatorModalOpen, setSeparatorModalOpen] = useState(false);
	const [selectedSeparator, setSelectedSeparator] = useState(',');
	const [loadedData, updateLoadedData] = useState(null)
	const [loading, setLoading] = useState(false)
	const [owner, setOwner] = useState(getSession().id)
	const [extraColumns, setExtraColumns] = useState([])

	const sortedUsersEmails = useMemo(() => (
		[...usersEmails].sort((a, b) => (
			(a.first_name || '').localeCompare(b.first_name || '', 'pt-BR', { sensitivity: 'base' }) ||
			(a.last_name || '').localeCompare(b.last_name || '', 'pt-BR', { sensitivity: 'base' })
		))
	), [usersEmails])

	useEffect(() => {
		if (!failedContacts || failedContacts.length === 0) return
		const rows = failedContacts.map((row, i) => ({
			...row,
			id: i,
			position: i,
			error: true,
		}))
		updateLoadedData(rows)
	}, [failedContacts])

	const handleReplicateSegments = () => {
		const newData = replicateFirstRowSegments(loadedData)
		if (newData) {
			updateLoadedData(newData)
		}
	};

	const RowItem = (props) => {
		const { index, style } = props;

		const item = loadedData.find(item => item.position === index)

		if (!item) {
			return (<></>)
		}
		return (
			<ListItem style={style} className={`import-contact-display-item item ${item.error ? 'error' : ''}`} key={`import-contact-item${item.id}`}>
				<RowEdit item={item} pos='nome' label='Editar nome' revalidate={() => updateLoadedData([...revalidate(loadedData)])} />
				<RowEdit item={item} pos='email' label='Editar email' revalidate={() => updateLoadedData([...revalidate(loadedData)])} />
				<RowEdit item={item} pos='telefone' label='Editar telefone' revalidate={() => updateLoadedData([...revalidate(loadedData)])} />
				<RowEdit item={item} pos='customer_code' label='Editar código' revalidate={() => updateLoadedData([...revalidate(loadedData)])} />
				<RowEdit item={item} pos='enterprise_name' label='Editar empresa' revalidate={() => updateLoadedData([...revalidate(loadedData)])} />
				<RowEdit item={item} pos='cnpj' label='Editar CNPJ' revalidate={() => updateLoadedData([...revalidate(loadedData)])} />
				<RowEdit item={item} pos='cpf' label='Editar CPF' revalidate={() => updateLoadedData([...revalidate(loadedData)])} />
				<RowEdit item={item} pos='segmentos' label='Editar segmentos' revalidate={() => updateLoadedData([...revalidate(loadedData)])} />
				<RowEdit item={item} pos='birthday' label='Editar aniversário' revalidate={() => updateLoadedData([...revalidate(loadedData)])} />
				<RowEdit item={item} pos='no_disturb' label='Editar não perturbe' revalidate={() => updateLoadedData([...revalidate(loadedData)])} />
				{extraColumns.map(col => (
					<RowEdit key={col.key} item={item} pos={col.key} label={`Editar ${col.label}`} revalidate={() => updateLoadedData([...revalidate(loadedData)])} />
				))}
				<span className='item-last'>
					{item.db_error && (
						<Tooltip title={item.db_error}>
							<ErrorOutlineIcon sx={{ color: 'error.main', fontSize: '1.2rem', cursor: 'help', alignSelf: 'center' }} />
						</Tooltip>
					)}
					<Tooltip title='Excluir linha '>
						<Button onClick={() => updateLoadedData(reposition(loadedData.filter(each => each !== item)))} className='action-button delete'>
							<DeleteIcon />
						</Button>
					</Tooltip></span>
			</ListItem>

		)
	}



	const onChangeImportClients = async (event) => {

		const oldCache = localStorage.getItem('uploadCache');

		if (oldCache) {
			useNotification(
				'Ops!',
				'Já existe uma importação em andamento..',
				'danger'
			);
			return
		}

		const newCache = Math.random()

		const newdata = new FormData()
		newdata.append('cache_key', newCache)

		const normalizedLoadedData = (loadedData || []).map((row) => ({
			...row,
			cnpj: normalizeCnpj(row.cnpj || '').slice(0, 14)
		}))

		const json = JSON.stringify(normalizedLoadedData);
		const blob = new Blob([json], {
			type: 'application/json'
		});

		newdata.append('data', blob)
		newdata.append('owner', owner === 'public' ? null : owner)

		updateLoadedData(null)
		setExtraColumns([])
		setFailedContacts([])

		importCSV(newdata, newCache);

		// Resetting file input
		event.target.value = null;
	}

	const importCSV = async (data, newcache) => {

		updateReady(false)

		const res = await api.post(`/api/enterprises/${enterpriseId}/contacts/upload_contacts/`, data, { timeout: 0 })

		updateReady(true)

		if (res.status === 200) {
			useNotification(
				'Por favor aguarde!',
				'A importação em massa pode demorar e ficará disponível na aba abaixo. Continue a usar o kwik normalmente.',
				'info'
			);
			setCache(newcache)
		}

		if (res.status === 403) {
			useNotification(
				'Ops!',
				'A importação em massa não é permitida para contas integradas com CRM.',
				'danger'
			);
		}

		if (res.status === 406) {
			useNotification(
				'Ops!',
				'Limite de contatos atingido. Contate o seu gerente comercial.',
				'danger'
			);
		}

		if (res.status === 500) {
			useNotification(
				'Ops!',
				'Ocorreu um erro interno no servidor.',
				'danger'
			);

		}

		if (res.status === 405) {
			useNotification(
				'Ops!',
				'Você não pode criar contatos.',
				'danger'
			);
		}
	}

	// const submitClientGroupForm = async () => {
	// 	updateShowAddClientGroupForm(false)
	// }

	const closeClientGroupForm = () => {

		// updateShowAddClientGroupForm(false)
		getClients()
		getGroups()
	}

	const importClientsButtonIsDisabled = () => {
		if (settings.is_financial_market_company && !permissions.isAdmin)
			return true
		return false
	}

	const errorCallback = useCallback((message) => {
		useNotification(
			'Ops!',
			message,
			'danger'
		)
	}, [])

	const clearNumber = value => {
		return value.replace(/[^0-9]+/g, '')
	}

	const possible_fields = {
		'nome': 0,
		'telefone': 0,
		'email': 0,
		'codigo': 0,
		'enterprise_name': 0,
		'segmentos': 0,
		'empresa': 0,
		'cpf': 0,
		'cnpj': 0,
		'birthday': 0,
		'aniversario': 0,
		'no_disturb': 0,
		'nao perturbe': 0,
		'não perturbe': 0
	}

	const extra_fields = {}

	const try_separators = [',', ';', '|']

	const detectSeparator = (data, separator_index) => {

		let real_separator = try_separators[separator_index]
		if (!real_separator)
			return false

		if (real_separator === ',')
			real_separator = /,(?=(?:[^"]*"[^"]*")*[^"]*$)/

		let count = 0

		Object.keys(extra_fields).forEach(key => delete extra_fields[key])

		data.split(real_separator).map((field, index) => {
			const selected_field = normalizeImportHeader(field)
			if (selected_field in possible_fields) {
				possible_fields[selected_field] = index + 1
				count++;
			} else if (selected_field && selected_field.trim()) {
				extra_fields[selected_field] = {
					index: index + 1,
					label: clearImportedText(field).replace(/['"]/g, '').trim()
				}
			}
		})


		if (count === 0)
			return detectSeparator(data, separator_index+1)
		return real_separator
	}

	const parseArray = (array) => {

		Object.keys(possible_fields).forEach((key) => {
			possible_fields[key] = 0
		})

		const rawdata = array.split(/\r?\n/)
		if (rawdata.length === 0) {
			setLoading(false)
			return errorCallback('Arquivo inválido')
		}

		const real_separator = detectSeparator(rawdata[0], 0)

		if (!real_separator) {
			setLoading(false)
			return errorCallback('Não há cabeçalhos, é necessário conter pelo menos nome e (telefone ou email) e o separador deve ser , ou ; ou |')
		}

		rawdata.shift()

		const modData = rawdata.map((row, index) => {

			if (row === '') return

			const separated_data = clearImportedText(row).split(real_separator)
			const data = ['', ...separated_data]
			const returndata = { id: index }
			const maxLength = data.length
			returndata.nome = data[possible_fields['nome']] ? data[possible_fields['nome']].trim() : ''
			returndata.email = data[possible_fields['email']] ? data[possible_fields['email']].trim() : ''
			returndata.telefone = data[possible_fields['telefone']] ? clearNumber(data[possible_fields['telefone']]) : ''

			if (possible_fields['codigo'])
				returndata.customer_code = data[possible_fields['codigo']] ? data[possible_fields['codigo']].trim() : ''

			if (possible_fields['enterprise_name']) {
				returndata.enterprise_name = data[possible_fields['enterprise_name']] ? data[possible_fields['enterprise_name']] : ''
			} else if (possible_fields['empresa']) {
				returndata.enterprise_name = data[possible_fields['empresa']] ? data[possible_fields['empresa']] : ''
			}

			if (possible_fields['cpf']) {
				returndata.cpf = data[possible_fields['cpf']] ? data[possible_fields['cpf']] : ''
			}

			returndata.segmentos = ''
			if (possible_fields['segmentos'] && possible_fields['segmentos'] in data) {
				returndata.segmentos = data[possible_fields['segmentos']].replaceAll('"', '') || ''
			}

			// new optional columns
			if (possible_fields['cnpj']) {
				returndata.cnpj = data[possible_fields['cnpj']] ? normalizeCnpj(data[possible_fields['cnpj']]).slice(0, 14) : ''
			}
			if (possible_fields['birthday'] || possible_fields['aniversario']) {
				const idx = possible_fields['birthday'] || possible_fields['aniversario']
				returndata.birthday = data[idx] ? data[idx].trim() : ''
			}
			if (possible_fields['no_disturb'] || possible_fields['nao perturbe'] || possible_fields['não perturbe']) {
				const idx2 = possible_fields['no_disturb'] || possible_fields['nao perturbe'] || possible_fields['não perturbe']
				returndata.no_disturb = data[idx2] ? data[idx2].trim() : ''
			}

			Object.entries(extra_fields).forEach(([fieldName, fieldInfo]) => {
				const fieldIdx = fieldInfo.index
				if (fieldIdx && fieldIdx < data.length) {
					returndata[fieldName] = data[fieldIdx] ? data[fieldIdx].trim() : ''
				}
			})

    	return returndata
		})

		const newdata = revalidate(modData.filter(d => d))

		updateLoadedData(reposition(newdata))
		setLoading(false)

		const cfNamesLower = (customFields || []).map(cf => normalizeImportHeader(cf.field_name))
		const detectedExtra = Object.entries(extra_fields)
			.filter(([, fieldInfo]) => fieldInfo.index > 0)
			.map(([fieldName, fieldInfo]) => {
				const isCustomField = cfNamesLower.includes(fieldName)
				return {
					key: fieldName,
					label: fieldInfo.label || fieldName,
					isCustomField
				}
			})
		setExtraColumns(detectedExtra)

	}

	const prepareData = (file) => {

		const oldCache = localStorage.getItem('uploadCache');

		if (oldCache) {
			useNotification(
				'Ops!',
				'Já existe uma importação em andamento..',
				'danger'
			);
			return
		}

		var reader = new FileReader();
		const eventFile = file;
		reader.onload = function (e) {
			let text

			try {
				text = readImportedFileAsText(eventFile, e.target.result)
			} catch (err) {
				setLoading(false)
				return errorCallback('Arquivo inválido')
			}

			parseArray(text)
		}
		reader.readAsArrayBuffer(eventFile);
		setLoading(true)
	}

	const addNovaEntrada = () => {
		if (loadedData.length >= importLimit) {
			return;
		}
		reposition
		const newitem = {
			nome: '',
			email: '',
			telefone: '',
			customer_code: '',
			enterprise_name: '',
			cnpj: '',
			cpf: '',
			segmentos: '',
			birthday: '',
			no_disturb: ''
		}
		extraColumns.forEach(col => { newitem[col.key] = '' })
		updateLoadedData(reposition(revalidate([newitem, ...loadedData])))
	}

	const errors = loadedData?.filter(each => each.error).length
	const validationErrors = loadedData?.filter(each => each.error && !each.db_error).length

	return (
		<>
			{createPortal((<Loading loading={loading} />), document.body)}
			{loadedData && <>
				<Dialog
					open={true}
					fullWidth
					maxWidth='xl'
					disableEnforceFocus
					className='import-contact-dialog'
				>
					<DialogTitle id="customized-dialog-title" className={'group-flexbox'}>
						<span>Importar contatos</span> <Button sx={{ 'minWidth': '2rem', 'padding': '0' }} onClick={() => { updateLoadedData(null); setExtraColumns([]); setFailedContacts([]) }} ><CloseIcon /></Button>
					</DialogTitle>
					<Divider />
					<DialogContent className='import-contact-content'>
						{loadedData?.some(r => r.db_error) && (
							<Box sx={{ color: 'warning.main', mb: 1, fontSize: '0.85rem' }}>
								Estes contatos falharam durante a importação. Corrija os dados e reenvie.
							</Box>
						)}
						<Box className='import-contact-display-item'>
							<Box>Quantidade de contatos: {loadedData.length} / {importLimit}</Box>
							<Box>Quantidade de contatos com erros: {errors}</Box>
						</Box>
						<Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
							<Box sx={{display:'flex', alignItems: 'center', gap:'.5rem'}}>
								<LoadingButton
									size="small"
									component="label"
									sx={{ alignSelf: 'self-start', height:'100%' }}
									startIcon={<AddCircleIcon />}
									onClick={addNovaEntrada}
									disabled={loadedData.length >= importLimit}
								>
									Adicionar
								</LoadingButton>
								{<FormControl fullWidth size="small" className='owner-box-max-size' sx={{ width: '15rem' }}>
									<InputLabel id="notification-opt-out-label">Dono</InputLabel>
									<Select
										labelId="notification-opt-out-label"
										value={owner}
										label="Dono"
										onChange={(event) => setOwner(event.target.value)}
									>
										<MenuItem id='user-item-none' value={'public'}>Público</MenuItem>
										{sortedUsersEmails.map((item) => (
											<MenuItem id={`user-item-${item.id}`} value={item.id}>{item.first_name} {item.last_name} - {item.username}</MenuItem>
										))}
									</Select>
								</FormControl>}
							</Box>
							<LoadingButton
								size="small"
								component="label"
								sx={{ alignSelf: 'flex-end' }}
								startIcon={<IconButton />}
								onClick={handleReplicateSegments}
							>
								Replicar segmentos
							</LoadingButton>
						</Box>
						<Box sx={{ height: "100%", display: 'flex', flexDirection: 'column' }}>
							<ListItem className='import-contact-display-item header' key={`import-contact-item`}>
								<span>Nome</span>
								<span>Email</span>
								<Tooltip title="Obrigatório, 10 a 15 caracteres, pode ou não começar com + ou +55" placement="bottom" disableInteractive>
									<span>
										<div className='import-contact-segment-icon'>Telefone <HelpIcon className="question-icon" /></div>
									</span>
								</Tooltip>
								<span>Código</span>
								<span>Empresa</span>
								<span>CNPJ</span>
								<span>CPF</span>
								<Tooltip title="Segmentos devem ser separados por vírgula." placement="bottom" disableInteractive>
									<span>
										<div className='import-contact-segment-icon'>Segmentos <HelpIcon className="question-icon" /></div>
									</span>
								</Tooltip>
								<span>Aniversário</span>
								<span>Não perturbe</span>

								{extraColumns.map(col => (
									<Tooltip
										key={col.key}
										title={col.isCustomField
											? `Campo customizado: ${col.label}`
											: `Coluna "${col.label}" não corresponde a nenhum campo do sistema. Será ignorada.`
										}
										placement="bottom"
										disableInteractive
									>
										<span>
											<div className='import-contact-segment-icon'>
												{col.isCustomField
													? <>{col.label} <HelpIcon className="question-icon" /></>
													: <><span style={{ color: '#d32f2f', textDecoration: 'line-through' }}>{col.label}</span> <WarningAmberIcon className="question-icon" sx={{ color: '#d32f2f' }} /></>
												}
											</div>
										</span>
									</Tooltip>
								))}

								<span className='item-last'></span>
							</ListItem>
							<Box sx={{ height: "100%" }}>
								<AutoSizer >
									{({ height, width }) =>
									(
										<>
											<FixedSizeList
												height={height}
												width={width}
												itemSize={40}
												itemCount={loadedData.length}
												overscanCount={10}
											>
												{RowItem}
											</FixedSizeList>
										</>
									)}
								</AutoSizer>
							</Box>
						</Box>
					</DialogContent>
					<DialogActions style={{ padding: "10px 20px 10px 10px" }}>
						<LoadingButton
							onClick={onChangeImportClients}
							color="primary"
							disabled={(validationErrors > 0 || loadedData.length === 0)}
						>
							Enviar
						</LoadingButton>
					</DialogActions>
				</Dialog>
			</>
			}

			<ListItem className={'menuItem'} sx={{ padding: 0 }}>
				<ResponsiveImportButton
					className="containedImportButton"
					onClickFn={() => {
						if (permissions && !permissions.isSupervisor && !permissions.isAdmin) {
								if (!getSession().profile.create_contacts) {
										useNotification(
												'Ops!',
												'Você não tem permissão para criar contatos.',
												'danger'
										);
								}
						}
					}}
					isDisabled={importClientsButtonIsDisabled()}
					title={"Importar"}
					shouldRenderHiddenInput={permissions && permissions.isSupervisor}
					onChangeFn={prepareData}
					is_financial_market_company={settings.is_financial_market_company}
				/>
			</ListItem>

		</>

	)
}

export default ImportClients
